import React from 'react';
import noResultsDesktop from '~/assets/images/search-background.svg';
import RESOURCES from '~/resources';
import { Typography, Box, SxProps } from '@mui/material';
import useStyles, {
  clearButtonsContainerStyle,
  clearButtonsStyle,
  NoResultImage,
  root,
} from './useStyles';
import {
  carFiltersMap,
  usedCarFiltersMap,
  filterKeyToLabel,
  filterKeyToLabelUsedCar,
} from './noResultMap';
import Button from '../Button';
import { BUTTON_SIZES, BUTTON_TYPES } from '~/data/constants';
import MinusIcon from '~/assets/icons/jsx/MinusIcon';
import { RootState } from '~/redux-toolkit/store/store';
import { useAppSelector } from '~/redux-toolkit/store/hooks';
import { useDispatch } from 'react-redux';
import {
  setFiltersBodyType,
  setFiltersBrands,
  setFiltersCategories,
  setFiltersColors,
  setFiltersHighLights,
  setFiltersModels,
  cleanAllFilters,
  cleanBudget,
} from '~/redux-toolkit/slices/resultPageSlice';
import {
  setFiltersBodyTypes as setFiltersBodyTypeUsedCar,
  setFiltersBrands as setFiltersBrandsUsedCar,
  setUsedCarFiltersCategories as setFiltersCategoriesUsedCar,
  setFiltersColors as setFiltersColorsUsedCar,
  setFiltersHighlights as setFiltersHighLightsUsedCar,
  setFiltersModels as setFiltersModelsUsedCar,
  cleanAllFilters as cleanAllFiltersUsedCar,
  cleanBudget as cleanBudgetUsedCar,
  setCarHand,
  setEngineType,
  setGearType,
  setOriginOwnership,
  setIsCarsOnlyWithImages,
  cleanKm,
  cleanManufactureYear,
} from '~/redux-toolkit/slices/usedCarFiltersSlice';
import { useIsUsedCar } from '~/hooks/useIsUsedCar';

type NoResultsProps = {
  customContainerStyles?: React.CSSProperties | SxProps;
  filterHeight?: string;
};

const NoResults = ({
  customContainerStyles,
  filterHeight = '',
}: NoResultsProps) => {
  const classes = useStyles(filterHeight);
  const dispatch = useDispatch();
  const { query } = useAppSelector((state: RootState) => state.resultPage);
  const isUsedCar = useIsUsedCar();
  const { usedCarQuery } = useAppSelector(
    (state: RootState) => state.usedCarFilters,
  );

  const filterKeyToFunc: { [key: string]: () => void } = {
    [carFiltersMap.BUDGET]: () => dispatch(cleanBudget()),
    [carFiltersMap.COLORS]: () => dispatch(setFiltersColors([])),
    [carFiltersMap.CATEGORIES]: () => dispatch(setFiltersCategories([])),
    [carFiltersMap.MODELS]: () => dispatch(setFiltersModels([])),
    [carFiltersMap.BRANDS]: () => dispatch(setFiltersBrands([])),
    [carFiltersMap.CAR_DESCRIPTION_HIGHLIGHTS]: () =>
      dispatch(setFiltersHighLights([])),
    [carFiltersMap.CAR_BODY_TYPE]: () => dispatch(setFiltersBodyType([])),
  };

  const filterKeyToFuncUsedCar: { [key: string]: () => void } = {
    [usedCarFiltersMap.BUDGET]: () => dispatch(cleanBudgetUsedCar()),
    [usedCarFiltersMap.COLORS]: () => dispatch(setFiltersColorsUsedCar([])),
    [usedCarFiltersMap.CATEGORIES]: () =>
      dispatch(setFiltersCategoriesUsedCar([])),
    [usedCarFiltersMap.MODELS]: () => dispatch(setFiltersModelsUsedCar([])),
    [usedCarFiltersMap.BRANDS]: () => dispatch(setFiltersBrandsUsedCar([])),
    [usedCarFiltersMap.CAR_HIGHLIGHTS]: () =>
      dispatch(setFiltersHighLightsUsedCar([])),
    [usedCarFiltersMap.CAR_BODY_TYPE]: () =>
      dispatch(setFiltersBodyTypeUsedCar([])),
    [usedCarFiltersMap.CAR_HAND]: () => dispatch(setCarHand([])),
    [usedCarFiltersMap.ENGINE_TYPE]: () => dispatch(setEngineType([])),
    [usedCarFiltersMap.GEAR_TYPE]: () => dispatch(setGearType([])),
    [usedCarFiltersMap.KM_RANGE]: () => dispatch(cleanKm()),
    [usedCarFiltersMap.ORIGINAL_OWNERSHIP]: () =>
      dispatch(setOriginOwnership([])),
    [usedCarFiltersMap.YEARS_RANGE]: () => dispatch(cleanManufactureYear()),
    [usedCarFiltersMap.WITH_IMAGE]: () =>
      dispatch(setIsCarsOnlyWithImages(null)),
  };

  return (
    <Box sx={{ ...root, ...customContainerStyles }}>
      <Box sx={classes.imageWrapper}>
        <NoResultImage src={noResultsDesktop.src} alt="no-results" />
      </Box>
      <Typography variant="subtitle1" sx={classes.label}>
        {RESOURCES.NO_RESULTS_FOUND_1}
      </Typography>
      <Typography variant="subtitle1" sx={classes.label}>
        {RESOURCES.NO_RESULTS_FOUND_2}
      </Typography>
      <Box sx={clearButtonsContainerStyle}>
        {!isUsedCar &&
          Object.entries(query.filters).map(([key, value]) => {
            const shouldRenderButton =
              (key === carFiltersMap.BUDGET &&
                value?.priceRange?.minPrice &&
                value?.priceRange?.maxPrice) ||
              (Array.isArray(value) && value.length > 0);

            return shouldRenderButton ? (
              <Button
                key={key}
                label={`${RESOURCES.FILTERS_CLEAR_FILTER} ${filterKeyToLabel[key]}`}
                type={BUTTON_TYPES.NO_RESULT}
                size={BUTTON_SIZES.FIT}
                icon={<MinusIcon />}
                onClickFunc={filterKeyToFunc[key]}
                customStyle={clearButtonsStyle}
              />
            ) : null;
          })}
        {isUsedCar &&
          Object.entries(usedCarQuery.filters).map(([key, value]) => {
            const shouldRenderButton =
              (key === usedCarFiltersMap.BUDGET &&
                value?.priceRange?.minPrice &&
                value?.priceRange?.maxPrice) ||
              (Array.isArray(value) && value.length > 0) ||
              (key === usedCarFiltersMap.WITH_IMAGE && value) ||
              (key === usedCarFiltersMap.YEARS_RANGE &&
                value?.minYear &&
                value?.maxYear) ||
              (key === usedCarFiltersMap.KM_RANGE &&
                value?.minPrice &&
                value?.maxPrice);

            return shouldRenderButton ? (
              <Button
                key={key}
                label={`${RESOURCES.FILTERS_CLEAR_FILTER} ${filterKeyToLabelUsedCar[key]}`}
                type={BUTTON_TYPES.NO_RESULT}
                size={BUTTON_SIZES.FIT}
                icon={<MinusIcon />}
                onClickFunc={filterKeyToFuncUsedCar[key]}
                customStyle={clearButtonsStyle}
              />
            ) : null;
          })}
      </Box>
      <Box>
        <Typography
          onClick={() =>
            dispatch(isUsedCar ? cleanAllFiltersUsedCar() : cleanAllFilters())
          }
          sx={classes.clear_all}
        >
          {RESOURCES.FILTERS_CLEAR_ALL_FILTER}
        </Typography>
      </Box>
    </Box>
  );
};

export default NoResults;
